import React, { useContext, useEffect } from "react";
import { useMsal, useAccount } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { callApiWithToken } from "fetch";
import { protectedResources } from "authConfig";
import { FormDataContext } from "context/FormDataContext";
import { ApiDataContext } from "context/ApiDataContext";
import Label from "elements/Label";
import styled from "styled-components";
import PropTypes from "prop-types";
import HelpText from "elements/HelpText";
import WorkScheduleField from "components/form/WorkScheduleField";
import Layout from "components/Layout";
import Button from "elements/Button";
import { useNavigate } from "react-router-dom";
import JobTitle from "components/JobTitle";

function getLabel(fieldValue, options) {
  const index = options?.findIndex((e) => e.value == fieldValue);
  if (fieldValue) return options[index].label;
}

function getValuesFromArray(fieldValue) {
  const values = [];
  fieldValue.map((x, key) => {
    values.push(
      <div key={key}>
        {x[0]}, {x[1]} {x[2]}
        <div>{x[3]}</div>
      </div>
    );
  });
  return values;
}

function formatDate(date) {
  return (
    date.getDate() +
    "." +
    (parseInt(date.getMonth()) + 1) +
    "." +
    date.getFullYear()
  );
}

function getValue(fieldValue, isMultiChoice, options, isDate) {
  let value = fieldValue;
  if (isMultiChoice) {
    value = getLabel(fieldValue, options);
  }
  if (isDate && fieldValue) {
    const myDate = new Date(fieldValue);
    value = formatDate(myDate);
  }
  return value;
}

function Field({
  fieldName,
  isMultiChoice,
  isTextArea,
  isDate,
  small,
  labelBasedOnJobType,
}) {
  const { employment, jobType } = useContext(FormDataContext);
  const { apiData, labels } = useContext(ApiDataContext);

  const fieldValue = employment?.[fieldName]?.value;
  const options = apiData[fieldName];

  const value = Array.isArray(fieldValue)
    ? getValuesFromArray(fieldValue)
    : getValue(fieldValue, isMultiChoice, options, isDate);

  let label;
  if (labelBasedOnJobType) {
    label = labels[fieldName][jobType] ?? labels[fieldName].default;
  } else {
    label = labels[fieldName];
  }

  return (
    <FieldWrapper>
      <LabelMaxWidth>{label}</LabelMaxWidth>
      <Value isTextArea={isTextArea} small={small}>
        {value}
      </Value>
    </FieldWrapper>
  );
}

const LabelMaxWidth = styled(Label)`
  max-width: 450px;
`;

function Schedule() {
  const { apiData, labels } = useContext(ApiDataContext);
  const { employment, jobType } = useContext(FormDataContext);

  const defaultWorkScheduleData = [
    { dayoff: false, notime: false, start: "", end: "", hoursTotal: 0 },
    { dayoff: false, notime: false, start: "", end: "", hoursTotal: 0 },
    { dayoff: false, notime: false, start: "", end: "", hoursTotal: 0 },
    { dayoff: false, notime: false, start: "", end: "", hoursTotal: 0 },
    { dayoff: false, notime: false, start: "", end: "", hoursTotal: 0 },
    { dayoff: false, notime: false, start: "", end: "", hoursTotal: 0 },
    { dayoff: true, notime: false, start: "", end: "", hoursTotal: 0 },
  ];

  return (
    <FieldWrapper>
      {employment?.workschedule_type?.value !== "" ? (
        <LabelMaxWidth>
          {employment?.weekly_hours?.value >= 37
            ? labels.supportTexts.fullTime[
                employment?.workschedule_type?.value
              ] ?? labels.supportTexts.fullTime.default
            : labels.supportTexts.parttime[
                employment?.workschedule_type?.value
              ] ?? labels.supportTexts.parttime.default}
        </LabelMaxWidth>
      ) : (
        <LabelMaxWidth>
          {employment?.weekly_hours?.value >= 37
            ? labels.supportTexts.fullTime[jobType] ??
              labels.supportTexts.fullTime.default
            : labels.supportTexts.parttime[jobType] ??
              labels.supportTexts.parttime.default}
        </LabelMaxWidth>
      )}
      <WorkScheduleField
        name="workSchedule"
        printversion
        options={apiData.days}
        value={
          employment?.workSchedule?.value.length > 0
            ? employment?.workSchedule?.value
            : defaultWorkScheduleData
        }
      />
    </FieldWrapper>
  );
}

function PurposeSalary() {
  const { apiData, labels } = useContext(ApiDataContext);
  const { employment } = useContext(FormDataContext);

  const options = apiData.purpose_salary.purpose;

  const purposeSalary = employment?.purpose_salary?.value.map((item, i) => {
    return (
      <tr key={i}>
        <TableData>{getValue(item[0], true, options, false)}</TableData>
        <TableData>{getValue(item[1])}</TableData>
        <TableData>{item[2]}</TableData>
      </tr>
    );
  });

  return (
    <FieldWrapper>
      <LabelMaxWidth>{labels.purpose_salary}</LabelMaxWidth>
      <Table>
        <thead>
          <tr>
            <TableHeader align="left">Formål</TableHeader>
            <TableHeader align="left">Procent</TableHeader>
            <TableHeader align="left">Bemærkninger</TableHeader>
          </tr>
        </thead>
        <tbody>{purposeSalary}</tbody>
      </Table>
    </FieldWrapper>
  );
}

function PrintPreview() {
  const { updateEmployment, currentOrganization } = useContext(FormDataContext);
  const { labels } = useContext(ApiDataContext);
  const { instance, accounts, inProgress } = useMsal();
  const account = useAccount(accounts[0] || {});
  const currentDate = formatDate(new Date());
  const jobType = window.location.pathname.split("/")[2]?.toLowerCase() || null;

  useEffect(() => {
    if (account && inProgress === "none" && currentOrganization) {
      const data = {
        jobType: jobType,
        organization: currentOrganization[0],
      };
      instance
        .acquireTokenSilent({
          scopes: protectedResources.getEmployment.scopes,
          account: account,
        })
        .then((response) => {
          callApiWithToken(
            response.accessToken,
            `${protectedResources.getEmployment.endpoint}`,
            "POST",
            data
          ).then((response) => updateEmployment(response));
        })
        .catch((error) => {
          // in case if silent token acquisition fails, fallback to an interactive method
          if (error instanceof InteractionRequiredAuthError) {
            if (account && inProgress === "none") {
              instance
                .acquireTokenPopup({
                  scopes: protectedResources.getEmployment.scopes,
                })
                .then((response) => {
                  callApiWithToken(
                    response.accessToken,
                    `${protectedResources.getEmployment.endpoint}`,
                    "POST",
                    data
                  ).then((response) => updateEmployment(response));
                })
                .catch((error) => console.log(error));
            }
          }
        });
    }
  }, [account, inProgress, instance, currentOrganization]);

  let navigate = useNavigate();
  const redirectToPage = (location) => {
    navigate(location);
  };

  if (currentOrganization.length > 0) {
    return (
      <Layout>
        <Button onClick={() => window.print()}>Udskriv</Button>
        <Button onClick={() => redirectToPage("/apron/" + jobType)}>
          Tilbage
        </Button>
        <Title>Den gode ansættelse</Title>
        <SubTitle>
          <JobTitle jobType={jobType} /> - {currentDate}
        </SubTitle>
        <Organization>{currentOrganization[1]}</Organization>
        <Header>{labels.steps[0].header}</Header>
        <Field fieldName="employerCVR" />
        <Field fieldName="employerName" />
        <Field fieldName="employerAddress" />
        <Field fieldName="employerZipcode" />
        <Field fieldName="employerCity" />
        <Field fieldName="employerWorkplace" />

        <PageBreak />

        <Header>{labels.steps[1].header}</Header>
        {jobType !== "individuel" && (
          <Field fieldName="flexjob" isMultiChoice />
        )}
        <Field fieldName="starttime_of_employment" isDate />
        <Field fieldName="employment_lasting" isMultiChoice />

        <Field fieldName="limited_to_event" />
        <Field fieldName="limited_to_date" isDate />

        {jobType === "individuel" && (
          <>
            <Field fieldName="remuneration" isMultiChoice />
            <Field fieldName="regulated_pay" isMultiChoice />
            <Field fieldName="agreed_pay" />
            <Field fieldName="pay_level" />
            <Field fieldName="pay_scale" isMultiChoice />
            <Field fieldName="pay_scale_step" />
            <Field fieldName="pay_scale_wage" />
            <Field fieldName="agreed_pay_non_regulated" />
            <Field fieldName="wages_besides_pay" />
            <Label legend>Tillæg 1</Label>
            <Field fieldName="regulated_wage_1" isMultiChoice />
            <Field fieldName="agreed_wage_1" />
            <Field fieldName="pay_level_wage_1" />
            <Label legend>Tillæg 2</Label>
            <Field fieldName="regulated_wage_2" isMultiChoice />
            <Field fieldName="agreed_wage_2" />
            <Field fieldName="pay_level_wage_2" />
          </>
        )}

        <Field fieldName="weekly_hours" labelBasedOnJobType />
        <Field fieldName="trialAgreement" isMultiChoice />

        {(jobType === "graver" || jobType === "kirketjener") && (
          <Field
            fieldName="hired_thirtydays"
            isMultiChoice
            labelBasedOnJobType
          />
        )}
        {jobType === "graver" && <Field fieldName="amu" isMultiChoice />}
        {(jobType === "graver" || jobType === "kirketjener") && (
          <>
            <Field fieldName="pay" />
            <Field fieldName="labor_union" isMultiChoice />
          </>
        )}
        {(jobType === "gartner" ||
          jobType === "gartnerimedarbejder" ||
          jobType === "hk") && (
          <Field fieldName="employment_location" isMultiChoice />
        )}
        {jobType === "hk" && (
          <>
            <Field fieldName="monthly_pay_step_hk_grp" isMultiChoice />
            <Field fieldName="advancement_date_step2" isDate />
          </>
        )}
        {(jobType === "gartner" || jobType === "gartnerimedarbejder") && (
          <Field fieldName="monthly_pay_step" isMultiChoice />
        )}
        {(jobType === "gartner" ||
          jobType === "gartnerimedarbejder" ||
          jobType === "hk") && (
          <>
            <Field fieldName="wage_supplement_quality" />
            <Field fieldName="wage_supplement_function" />
          </>
        )}

        <PageBreak />

        <Header>{labels.steps[2].header}</Header>
        <Field fieldName="candidateCprnr" />
        <Field fieldName="candidateName" />
        <Field fieldName="candidateAddress" />
        <Field fieldName="candidateZipCode" />
        <Field fieldName="candidateCity" />
        <Field fieldName="candidateNotes" isTextArea />

        <PageBreak />

        {jobType !== "individuel" && (
          <>
            <Header>{labels.steps[3].header}</Header>
            <Field fieldName="age70" isMultiChoice />
            <Field fieldName="handlePension" isMultiChoice />
            <Field fieldName="other_pension" />
            <Field fieldName="age25_atleast4years" isMultiChoice />
            <Label>Hvor længe har du været ansat i branchen?</Label>
            <Field fieldName="years_in_business" />
            <Field fieldName="months_in_business" />
            <Field fieldName="age20_atleast9months" isMultiChoice />
            <Field fieldName="age20_payed_pension" isMultiChoice />
            <Label addTopMargin>Gruppeliv</Label>
            <StartAndEndDate>
              <Field fieldName="pension_gruppeliv_startdate" isDate small />
              <Field fieldName="pension_gruppeliv_enddate" isDate small />
            </StartAndEndDate>
            <Label addTopMargin>Minipension</Label>
            <StartAndEndDate>
              <Field fieldName="pension_mini_startdate" isDate small />
              <Field fieldName="pension_mini_enddate" isDate small />
            </StartAndEndDate>
            <Field fieldName="date_full_pension" isDate />
          </>
        )}

        <PageBreak />

        {/* Remove step 5 - Om stillingen for these jobtypes */}
        {jobType !== "individuel" &&
          jobType !== "gartner" &&
          jobType !== "gartneriarbejder" &&
          jobType !== "hk" && (
            <>
              <Header>{labels.steps[4].header}</Header>
              <Field
                fieldName="geographic_obligation"
                isMultiChoice
                labelBasedOnJobType
              />
              <Field fieldName="secondary_workplace" />
              <Field
                fieldName="professional_obligation"
                labelBasedOnJobType
                isMultiChoice
              />
              <Field
                fieldName="professional_obligation_positions"
                labelBasedOnJobType
              />
              <Field fieldName="availability_surcharge" isMultiChoice />
              <Field fieldName="availability_surcharge_type" isMultiChoice />
              {(jobType === "graver" || jobType === "kirketjener") && (
                <HelpText>
                  {labels.supportTexts.availability_surcharge[jobType]}
                </HelpText>
              )}
            </>
          )}

        <PageBreak />

        <Header>{labels.steps[5].header}</Header>
        <PurposeSalary />
        <Field fieldName="employee_handbook" isMultiChoice />
        <Label addTopMargin>Aktuel kontaktperson</Label>
        <Field fieldName="contactName" />
        <Field fieldName="contactPhoneNumber" />
        <Field fieldName="contactEmail" />
        <Schedule />
        <Field fieldName="workScheduleAdditionalNotes" isTextArea />
        <Field fieldName="dealsNotes" isTextArea />
        <Signature>
          <Label>{labels.signature.employer}</Label>
          <Label>{labels.signature.date}</Label>
          <Label>{labels.signature.signature}</Label>
        </Signature>
        <Signature>
          <Label>{labels.signature.employee}</Label>
          <Label>{labels.signature.date}</Label>
          <Label>{labels.signature.signature}</Label>
        </Signature>
        <Button onClick={() => window.print()}>Udskriv</Button>
      </Layout>
    );
  } else {
    return <div>indlæser...</div>;
  }
}

const Signature = styled.div`
  display: flex;
  border-bottom: 1px solid #ccc;
  margin: 4rem 0;
  justify-content: space-between;
  font-size: 0.95em;
`;

const FieldWrapper = styled.div`
  margin: 1em 0;
`;

const Title = styled.h1`
  margin: 0;
`;

const Organization = styled.div`
  margin: 0 0 1em 0;
`;

const SubTitle = styled.h2`
  margin: 0;
  font-weight: normal;
  text-transform: capitalize;
`;

const Header = styled.h2`
  font-size: 1.25em;
  margin-top: 2em;
`;

const StartAndEndDate = styled.div`
  display: flex;
  gap: 1em;
`;

const Table = styled.table`
  border-collapse: collapse;
  border: 1px solid;
`;

const TableHeader = styled.th`
  font-size: 12px;
  text-align: left;
  font-weight: normal;
  padding: 1em;
`;

const TableData = styled.td`
  font-size: 12px;
  border: 1px solid;
  padding: 1em;
`;

const Value = styled.div`
  border: 1px solid #ccc;
  border-left: ${(props) => (props.isTextArea ? "1" : "none")};
  border-right: ${(props) => (props.isTextArea ? "1" : "none")};
  border-top: ${(props) => (props.isTextArea ? "1" : "none")};
  padding: ${(props) => (props.isTextArea ? "0.5em" : "0.5em 0 .125em 0")};
  width: ${(props) => (props.small ? "200px" : "450px")};
  font-size: 0.95em;
  min-height: ${(props) => (props.isTextArea ? "100px" : "20px")};
`;

const PageBreak = styled.div`
  page-break-before: always;
`;

Field.propTypes = {
  fieldName: PropTypes.string,
  isMultiChoice: PropTypes.bool,
  isTextArea: PropTypes.bool,
  isDate: PropTypes.bool,
  small: PropTypes.bool,
  labelBasedOnJobType: PropTypes.bool,
};

PurposeSalary.propTypes = {
  employment: PropTypes.object,
};

export default PrintPreview;
